import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import CheckIcon from './assets/checkmark.svg?react';
import { Interactive } from '../typography';

export const Label = styled.label`
    display: inline-flex;
    flex-direction: ${(props) => (props.labelSide === 'left' ? 'row' : 'row-reverse')};
    align-items: center;
    cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};

    & > *:not(:last-child) {
        margin-right: ${(props) => (props.labelSide === 'left' ? '8px' : '0')};
        margin-left: ${(props) => (props.labelSide === 'left' ? '0' : '8px')};
    }
`;

export const SwitchCointainer = styled.div`
    position: relative;
    display: inline-block;
    width: 32px;
    height: 18px;
`;

export const SliderThumb = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    height: 16px;
    width: 16px;
    left: 1px;
    bottom: 1px;
    background-color: ${(props) => props.theme.colors['surface-white']};
    border-radius: 7px;
    transition: 0.4s;
    box-shadow: ${(props) => props.theme.shadows['shadow-01dp']};
`;

export const SliderTrack = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    cursor: pointer;
    background-color: ${(props) => props.theme.colors['surface-grey-700']};
    transition: 0.4s;
    border-radius: 8px;
`;

export const Checkbox = styled.input`
    opacity: 0;
    width: 0;
    height: 0;

    & + ${SliderTrack} > ${SliderThumb} > svg {
        width: 12px;
        height: 12px;
        fill: ${(props) => props.theme.colors['myzanichelli-blue-main']};
        opacity: 0;
        transform: rotate(-30deg);
    }

    &:checked + ${SliderTrack} > ${SliderThumb} {
        transform: translateX(14px);
    }

    &:checked + ${SliderTrack} > ${SliderThumb} > svg {
        opacity: 1;
        transform: rotate(0deg);
        transition-delay: 0.2s;
        transition: 0.4s;
    }

    &:checked + ${SliderTrack} {
        background-color: ${(props) => props.theme.colors['myzanichelli-blue-main']};
        transition: 0.4s;
    }

    &:hover + ${SliderTrack} {
        background-color: ${(props) => props.theme.colors['myzanichelli-blue-dark']};
    }

    &:hover + ${SliderTrack} > ${SliderThumb} > svg {
        fill: ${(props) => props.theme.colors['myzanichelli-blue-dark']};
    }

    &:disabled + ${SliderTrack} {
        background-color: ${(props) => props.theme.colors['surface-grey-200']};
        cursor: initial;
    }
`;

/**
 * A Switch represents a button with two states, on and off.
 * Switches are most often used to enable and disable options.
 * A switch consists of a track and thumb:
 * the thumb moves along the track to indicate its current state.
 * @param {*} props label, value, onToggle
 */
const Switch = ({ label = '', value = false, onToggle = (v) => v, labelSide = 'left', disabled = false }) => {
    const [state, setState] = useState(value);

    /**
     * Observe value change.
     */
    useEffect(() => {
        setState(value);
    }, [value]);

    /**
     * Calls when switch is clicked
     * @param {*} event javascript event
     */
    const onChange = (e) => {
        setState(e.target.checked);
        onToggle(e.target.checked);
    };

    return (
        <Label labelSide={labelSide} disabled={disabled}>
            {label && (
                <Interactive color={disabled ? 'disabled' : 'enabled-grey'} disabled={disabled}>
                    {label}
                </Interactive>
            )}
            <SwitchCointainer>
                <Checkbox type="checkbox" checked={state} onChange={onChange} disabled={disabled} />
                <SliderTrack>
                    <SliderThumb>
                        <CheckIcon />
                    </SliderThumb>
                </SliderTrack>
            </SwitchCointainer>
        </Label>
    );
};

Switch.propTypes = {
    label: PropTypes.string,
    value: PropTypes.bool,
    onToggle: PropTypes.func,
    labelSide: PropTypes.oneOf(['left', 'right']),
    disabled: PropTypes.bool,
};

export default Switch;
